/**
 * ConfidenceIndicator - Visual indicators for fields with confidence scores
 * Based on requirements 2.1, 2.2, 2.3
 */

import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// Styled chip for confidence display
const ConfidenceChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'confidenceLevel',
})<{ confidenceLevel: 'low' | 'medium' | 'high' }>(({ theme, confidenceLevel }) => {
  const getColors = () => {
    switch (confidenceLevel) {
      case 'low':
        return {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText,
          borderColor: theme.palette.error.main,
        };
      case 'medium':
        return {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.contrastText,
          borderColor: theme.palette.warning.main,
        };
      case 'high':
        return {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.contrastText,
          borderColor: theme.palette.success.main,
        };
      default:
        return {
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.text.primary,
          borderColor: theme.palette.grey[400],
        };
    }
  };

  const colors = getColors();
  
  return {
    ...colors,
    border: `1px solid ${colors.borderColor}`,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-icon': {
      color: colors.color,
    },
  };
});

export interface ConfidenceIndicatorProps {
  confidence: number;
  showLabel?: boolean;
  size?: 'small' | 'medium';
  variant?: 'icon' | 'chip' | 'text';
  className?: string;
}

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  confidence,
  showLabel = true,
  size = 'small',
  variant = 'chip',
  className,
}) => {
  const theme = useTheme();

  // Determine confidence level and appropriate styling
  const getConfidenceLevel = (score: number): 'low' | 'medium' | 'high' => {
    if (score < 60) return 'low';
    if (score < 80) return 'medium';
    return 'high';
  };

  const confidenceLevel = getConfidenceLevel(confidence);

  // Get appropriate icon based on confidence level
  const getIcon = (): React.ReactElement | undefined => {
    switch (confidenceLevel) {
      case 'low':
        return <ErrorIcon fontSize={size} />;
      case 'medium':
        return <WarningIcon fontSize={size} />;
      case 'high':
        return <CheckCircleIcon fontSize={size} />;
      default:
        return undefined;
    }
  };

  // Get tooltip message
  const getTooltipMessage = () => {
    const baseMessage = `Confidence: ${confidence}%`;
    
    switch (confidenceLevel) {
      case 'low':
        return `${baseMessage} - Low confidence, please review carefully`;
      case 'medium':
        return `${baseMessage} - Medium confidence, verification recommended`;
      case 'high':
        return `${baseMessage} - High confidence`;
      default:
        return baseMessage;
    }
  };

  // Get display label
  const getLabel = () => {
    if (!showLabel) return '';
    return `${confidence}%`;
  };

  // Render based on variant
  const renderIndicator = () => {
    switch (variant) {
      case 'icon':
        return (
          <Tooltip title={getTooltipMessage()} placement="top" arrow>
            <span className={className}>
              {getIcon()}
            </span>
          </Tooltip>
        );

      case 'text':
        return (
          <Tooltip title={getTooltipMessage()} placement="top" arrow>
            <span
              className={className}
              style={{
                color: theme.palette[confidenceLevel === 'low' ? 'error' : confidenceLevel === 'medium' ? 'warning' : 'success'].main,
                fontWeight: 600,
                fontSize: size === 'small' ? '0.75rem' : '0.875rem',
              }}
            >
              {getLabel()}
            </span>
          </Tooltip>
        );

      case 'chip':
      default:
        return (
          <Tooltip title={getTooltipMessage()} placement="top" arrow>
            <ConfidenceChip
              icon={getIcon()}
              label={getLabel()}
              confidenceLevel={confidenceLevel}
              size={size}
              className={className}
              variant="outlined"
            />
          </Tooltip>
        );
    }
  };

  return renderIndicator();
};

export default ConfidenceIndicator;

// Export utility function for external use
export const getConfidenceLevel = (confidence: number): 'low' | 'medium' | 'high' => {
  if (confidence < 60) return 'low';
  if (confidence < 80) return 'medium';
  return 'high';
};

// Export confidence thresholds for consistency
export const CONFIDENCE_THRESHOLDS = {
  LOW: 60,
  MEDIUM: 80,
  HIGH: 100,
} as const;